<script setup lang="ts">
import { useUserStore } from "~/stores/user"
import { BottomLinksProps } from "./BottomLinks.props"
import { useMenuStore } from "~/stores/menu"
import { customPaths } from "~/utils/constants"
import { navigateToFindOrder } from "~/utils/navigateToFindOrder"
import type { MethodsType } from "~/composables/useGA4SiteEvents"
import { navigateToLoginSignupPage } from "~/utils/loginSignup/loginSignup"
import { pushMenuClickCtaEvent } from "~/utils/analytics/clickCta"

defineProps<BottomLinksProps>()

const userStore = useUserStore()
const menuStore = useMenuStore()

const authToken = useStatefulCookie("authToken")
const refreshToken = useStatefulCookie("refreshToken")
const commerceLayerToken = useStatefulCookie("cmlToken")
const route = useRoute()

const handleCloseSidebar = (iconType: keyof MethodsType | null = null) => {
  menuStore.openCloseSidebar(false)
  // navigateTo() is needed to make sure that query params are updated even if the cta is clicked when already inside the '/contatti' path
  if (route.fullPath.startsWith(customPaths.contactUs)) {
    if (iconType === "customerServiceCall") {
      navigateTo(customPaths.contactUs)
    }
    if (iconType === "customerServiceChat") {
      navigateTo(
        `${customPaths.contactUs}?help_subject=${helpSubjectParams.consulting_pharma}`
      )
    }
  }
}

const handleLogout = async () => {
  authToken.value = null
  refreshToken.value = null
  commerceLayerToken.value = null
  await userStore.logout()
  handleCloseSidebar()
}

const handleOrdersClick = (label: string) => {
  navigateToFindOrder(label)
  handleCloseSidebar()
}

const handleLoginSignup = () => {
  handleCloseSidebar()
  navigateToLoginSignupPage()
}

const handleNavigateToDiscountCode = (label: string) => {
  handleCloseSidebar()
  pushMenuClickCtaEvent(label)
}

const handleNavigateToPaybackCard = (label: string) => {
  handleCloseSidebar()
  pushMenuClickCtaEvent(label)
}
</script>

<!-- TODO: possible improvement: do not repeat all these handleCloseSidebar()  -->
<template>
  <div class="bottom-links-wrapper">
    <div
      class="
        discounts-code-wrapper
        bg-strawberry-20
        text-strawberry-200
        hover:text-strawberry-100
        flex
        justify-start
        px-4
        py-3
      "
    >
      <UtilsButton
        @click="
          () =>
            handleNavigateToDiscountCode(
              $t('hamburgerMenu.menuContent.discountCode')
            )
        "
        :text="$t('hamburgerMenu.menuContent.discountCode')"
        class="custom-btn-bg-transparent cursor-pointer"
        :path="customPaths.discounts"
      >
        <template #icon>
          <IconsPricetag class="text-strawberry-200 h-6 w-6" />
        </template>
      </UtilsButton>
    </div>
    <div
      class="
        discounts-code-wrapper
        bg-blue-20
        text-blue-200
        hover:text-blue-100
        flex
        justify-start
        px-4
        py-3
      "
    >
      <UtilsButton
        @click="
          () =>
            handleNavigateToPaybackCard(
              $t('hamburgerMenu.menuContent.paybackCard')
            )
        "
        :text="$t('hamburgerMenu.menuContent.paybackCard')"
        class="custom-btn-bg-transparent cursor-pointer"
        :path="customPaths.payback"
      >
        <template #icon>
          <IconsPayback class="h-6 w-6" />
        </template>
      </UtilsButton>
    </div>

    <div
      class="
        call-to-action-wrapper
        bg-slate-main
        flex flex-col
        items-start
        px-4
        py-2
      "
    >
      <div v-if="isLogged" class="flex w-full justify-between gap-4 py-4">
        <UtilsButton
          @click="handleCloseSidebar"
          :path="customPaths.profile.root"
          :text="$t('dialogsAndModals.welcome.myProfile')"
          class="
            custom-btn-gap-4
            custom-btn-underlined-link-white
            custom-btn-hover-yellow
          "
        >
          <template #icon>
            <IconsUserLogged class="h-6 w-6" />
          </template>
        </UtilsButton>

        <UtilsButton
          type="button"
          @click="handleLogout"
          order="last"
          :text="$t('hamburgerMenu.menuContent.exit')"
          class="custom-btn-link-white custom-btn-hover-yellow"
        >
          <template #icon>
            <IconsExit class="h-5 w-5" />
          </template>
        </UtilsButton>
      </div>
      <div v-else class="flex items-center gap-1 py-4">
        <UtilsButton
          type="button"
          @click="handleLoginSignup"
          :text="$t('hamburgerMenu.menuContent.logIn')"
          class="
            custom-btn-gap-4
            custom-btn-underlined-link-white
            custom-btn-hover-yellow
          "
        >
          <template #icon>
            <IconsUser class="h-6 w-6" />
          </template>
        </UtilsButton>

        <span class="pig-medium text-white">
          {{ $t("hamburgerMenu.menuContent.or") }}
        </span>

        <UtilsButton
          type="button"
          @click="handleLoginSignup"
          :text="$t('textBlock.utility.register')"
          class="custom-btn-underlined-link-white custom-btn-hover-yellow"
        />
      </div>

      <UtilsButton
        @click="
          () =>
            handleOrdersClick($t('hamburgerMenu.menuContent.whereIsMyOrder'))
        "
        :text="$t('hamburgerMenu.menuContent.whereIsMyOrder')"
        class="
          custom-btn-gap-4
          custom-btn-underlined-link-white
          custom-btn-hover-yellow
          py-4
        "
      >
        <template #icon>
          <IconsCar class="h-6 w-6" />
        </template>
      </UtilsButton>
    </div>

    <div
      class="
        support-button-wrapper
        bg-slate-800
        flex flex-col
        items-start
        gap-6
        px-4
        py-5
      "
    >
      <p class="dolphin-bold text-white">
        {{ $t("hamburgerMenu.menuContent.needHelp") }}
      </p>

      <UtilsButton
        @click="handleCloseSidebar('customerServiceFaq')"
        :text="$t('hamburgerMenu.menuContent.lookFaq')"
        class="custom-btn-underlined-link-white custom-btn-hover-yellow"
        :path="customPaths.faqs"
      />
      <UtilsButton
        @click="handleCloseSidebar('customerServiceChat')"
        :text="$t('hamburgerMenu.menuContent.askToFarmacist')"
        class="custom-btn-underlined-link-white custom-btn-hover-yellow"
        :path="`${customPaths.contactUs}?help_subject=${helpSubjectParams.consulting_pharma}`"
      />
      <UtilsButton
        @click="handleCloseSidebar('customerServiceCall')"
        :text="$t('hamburgerMenu.menuContent.chatWithClientService')"
        class="custom-btn-underlined-link-white custom-btn-hover-yellow"
        :path="customPaths.contactUs"
      />
    </div>
  </div>
</template>
